import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import { api, fmtINR, fmtNum, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, Ruler, Wand2, AlertTriangle, ArrowLeftRight, Zap } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import MezzanineTopView from "@/components/MezzanineTopView";

const defaults = {
  height_mm: 3000,
  length_mm: 9000,
  width_mm: 6000,
  vv_gap_mm: 3000,
  vv_gap_L_mm: 3000,
  vv_gap_W_mm: 3000,
  main_beam_axis: "auto",
  sec_target_mm: 600,
  staircases: 1,
  staircase_sides: ["front"],
  // Material thicknesses (mm) — user-selectable
  pillar_thickness_mm: 2,
  main_beam_thickness_mm: 2.5,
  flooring_thickness_mm: 2,
  railing_required: true,
  railing_front: false,
  railing_back: true,
  railing_left: true,
  railing_right: true,
  railing_custom_m: 0,
};

function Num({ label, unit, value, onChange, testid, hint, min = 0 }) {
  return (
    <div>
      <label className="label-tech mb-2 flex items-center justify-between">
        <span>{label}</span>
        {unit && <span className="text-ink-faint normal-case tracking-normal mono">{unit}</span>}
      </label>
      <input
        type="number"
        min={min}
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="input-tech mono"
        data-testid={testid}
      />
      {hint && <div className="text-[0.7rem] text-ink-muted mt-1.5">{hint}</div>}
    </div>
  );
}

function Seg({ value, onChange, options, testid }) {
  return (
    <div className="grid grid-cols-3 gap-px bg-line border border-line" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={`px-3 py-2 text-xs font-bold uppercase tracking-wider mono transition-colors ${
            value === o.v ? "bg-ink text-white" : "bg-white text-ink-muted hover:bg-surfaceAlt"
          }`}
          data-testid={`${testid}-${o.v}`}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

function ThicknessSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <div className="text-[0.6rem] uppercase tracking-wider text-ink-muted mb-1.5 mono">{label}</div>
      <select
        className="input-tech mono py-1.5 text-xs w-full"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o} value={o}>{o} mm</option>
        ))}
      </select>
    </div>
  );
}

const SIDES = [
  { v: "front", l: "Front" },
  { v: "back", l: "Back" },
  { v: "left", l: "Left" },
  { v: "right", l: "Right" },
];

export default function Mezzanine() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDealer = user?.role === "dealer";

  const [cfg, setCfg] = useState(defaults);
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [client, setClient] = useState({ name: "", company: "", contact: "", address: "", gstin: "" });
  const [notes, setNotes] = useState("");

  const update = (k) => (v) => setCfg((p) => ({ ...p, [k]: v }));

  // Manual compute (button-triggered)
  const compute = async () => {
    if (!cfg.length_mm || !cfg.width_mm || !cfg.height_mm || !cfg.vv_gap_mm) {
      toast.error("Please fill length, width, height and column spacing");
      return;
    }
    setComputing(true);
    try {
      const { data } = await api.post("/mezzanine/calculate", cfg);
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setComputing(false);
    }
  };

  // After a result exists, lightly auto-update on edits (debounced) so the layout drawing
  // stays in sync without forcing the user to re-click compute.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (!result) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/mezzanine/calculate", cfg);
        setResult(data);
      } catch (e) {
        // silent on debounce
      }
    }, 450);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  const suggestForAxis = async (axis) => {
    try {
      const total = axis === "L" ? cfg.length_mm : cfg.width_mm;
      const { data } = await api.post("/mezzanine/suggest_axis", { total_mm: total });
      const key = axis === "L" ? "vv_gap_L_mm" : "vv_gap_W_mm";
      setCfg((p) => ({ ...p, [key]: data.spacing_mm }));
      const dim = axis === "L" ? "length" : "width";
      const note = data.perfect ? "perfect division" : "closest divisor";
      toast.success(
        `${dim.toUpperCase()}: spacing ${data.spacing_mm}mm → ${data.bays} bays · ${note}`
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const setStaircaseCount = (n) => {
    setCfg((p) => {
      const arr = [...(p.staircase_sides || [])];
      while (arr.length < n) arr.push("front");
      arr.length = n;
      return { ...p, staircases: n, staircase_sides: arr };
    });
  };

  const setStaircaseSide = (idx, side) => {
    setCfg((p) => {
      const arr = [...(p.staircase_sides || [])];
      arr[idx] = side;
      return { ...p, staircase_sides: arr };
    });
  };

  const save = async () => {
    if (!result) {
      toast.error("Compute the configuration first");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/mezzanine/quotations", { client, config: cfg, notes });
      toast.success(`Mezzanine ${data.quote_number} saved`);
      navigate(`/mezzanine-quotations/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="Mezzanine · Configurator"
        title="Mezzanine Structure"
        subtitle="Step 1: enter dimensions · Step 2: layout auto-generates · Step 3: edit · Step 4: save quote."
      />

      <div className="p-8 grid grid-cols-1 xl:grid-cols-5 gap-px bg-line border border-line">
        {/* INPUT PANEL */}
        <section className="xl:col-span-2 bg-white p-6" data-testid="mz-inputs">
          <div className="flex items-center gap-2 mb-5">
            <Ruler size={18} className="text-brand" />
            <div className="label-tech">1 · Dimensions</div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <Num label="Length" unit="mm" value={cfg.length_mm} onChange={update("length_mm")} testid="mz-length" />
            <Num label="Width" unit="mm" value={cfg.width_mm} onChange={update("width_mm")} testid="mz-width" />
            <Num label="Height" unit="mm" value={cfg.height_mm} onChange={update("height_mm")} testid="mz-height" />
            <div />
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <div>
              <Num
                label="Length spacing"
                unit="mm"
                value={cfg.vv_gap_L_mm}
                onChange={update("vv_gap_L_mm")}
                testid="mz-gap-L"
                hint="Target along length"
                min={1000}
              />
              <button
                type="button"
                onClick={() => suggestForAxis("L")}
                className="btn-ghost w-full mt-2 py-1.5 text-[0.7rem]"
                data-testid="mz-suggest-L"
              >
                <Wand2 size={12} /> Suggest spacing length
              </button>
            </div>
            <div>
              <Num
                label="Width spacing"
                unit="mm"
                value={cfg.vv_gap_W_mm}
                onChange={update("vv_gap_W_mm")}
                testid="mz-gap-W"
                hint="Target along width"
                min={1000}
              />
              <button
                type="button"
                onClick={() => suggestForAxis("W")}
                className="btn-ghost w-full mt-2 py-1.5 text-[0.7rem]"
                data-testid="mz-suggest-W"
              >
                <Wand2 size={12} /> Suggest spacing width
              </button>
            </div>
          </div>

          <div className="flex gap-2 mt-5">
            <button
              onClick={compute}
              disabled={computing}
              type="button"
              className="btn-primary w-full"
              data-testid="mz-calc-btn"
            >
              <Zap size={14} /> {computing ? "Computing…" : "Compute mezzanine"}
            </button>
          </div>

          <div className="mt-7 pt-5 border-t border-line">
            <div className="label-tech mb-3 flex items-center gap-2">
              <ArrowLeftRight size={14} /> 3 · Edit layout
            </div>
            <div className="space-y-4">
              <div>
                <div className="label-tech text-[0.65rem] mb-2">Main beam direction</div>
                <Seg
                  testid="mz-axis"
                  value={cfg.main_beam_axis}
                  onChange={update("main_beam_axis")}
                  options={[
                    { v: "auto", l: "Auto" },
                    { v: "L", l: "Along length" },
                    { v: "W", l: "Along width" },
                  ]}
                />
              </div>

              <div>
                <div className="label-tech text-[0.65rem] mb-2">Material thickness (mm)</div>
                <div className="grid grid-cols-3 gap-2">
                  <ThicknessSelect
                    label="Vertical"
                    value={cfg.pillar_thickness_mm}
                    onChange={(v) => update("pillar_thickness_mm")(v)}
                    options={[2, 2.5, 3]}
                    testid="mz-thk-vertical"
                  />
                  <ThicknessSelect
                    label="Main beam"
                    value={cfg.main_beam_thickness_mm}
                    onChange={(v) => update("main_beam_thickness_mm")(v)}
                    options={[2, 2.5, 3]}
                    testid="mz-thk-mainbeam"
                  />
                  <ThicknessSelect
                    label="Flooring"
                    value={cfg.flooring_thickness_mm}
                    onChange={(v) => update("flooring_thickness_mm")(v)}
                    options={[1.6, 2, 2.5]}
                    testid="mz-thk-flooring"
                  />
                </div>
                <div className="text-[0.7rem] text-ink-muted mt-1.5">
                  Affects component weights & total quotation price.
                </div>
              </div>

              <SecondaryBeamPicker cfg={cfg} setCfg={setCfg} update={update} />

              <div>
                <div className="label-tech text-[0.65rem] mb-2">Staircases · count</div>
                <Seg
                  testid="mz-stairs"
                  value={cfg.staircases}
                  onChange={setStaircaseCount}
                  options={[
                    { v: 0, l: "0" },
                    { v: 1, l: "1" },
                    { v: 2, l: "2" },
                  ]}
                />
                {cfg.staircases > 0 && (
                  <div className="mt-3 space-y-2 pl-4 border-l-2 border-brand">
                    {Array.from({ length: cfg.staircases }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs mono uppercase tracking-wider w-20">Stair {i + 1}</span>
                        <select
                          className="input-tech mono py-1.5 text-xs flex-1"
                          value={cfg.staircase_sides?.[i] || "front"}
                          onChange={(e) => setStaircaseSide(i, e.target.value)}
                          data-testid={`mz-stair-side-${i}`}
                        >
                          {SIDES.map((s) => (
                            <option key={s.v} value={s.v}>
                              {s.l}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="label-tech text-[0.65rem] mb-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={cfg.railing_required}
                    onChange={(e) => setCfg({ ...cfg, railing_required: e.target.checked })}
                    data-testid="mz-railing-required"
                  />
                  Railing required
                </div>
                {cfg.railing_required && (
                  <div className="grid grid-cols-2 gap-1.5 pl-4 border-l-2 border-brand">
                    {[
                      { k: "railing_front", l: "Front (length)" },
                      { k: "railing_back", l: "Back (length)" },
                      { k: "railing_left", l: "Left (width)" },
                      { k: "railing_right", l: "Right (width)" },
                    ].map((o) => (
                      <label key={o.k} className="text-xs flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={cfg[o.k]}
                          onChange={(e) => setCfg({ ...cfg, [o.k]: e.target.checked })}
                          data-testid={`mz-${o.k}`}
                        />
                        {o.l}
                      </label>
                    ))}
                    <div className="col-span-2 mt-2">
                      <Num
                        label="Or custom running metres"
                        unit="m"
                        value={cfg.railing_custom_m}
                        onChange={update("railing_custom_m")}
                        testid="mz-railing-custom"
                        hint="Overrides sides if > 0"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-7 pt-5 border-t border-line">
            <div className="label-tech mb-3">
              Client details <span className="text-ink-faint normal-case tracking-normal">(optional)</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                className="input-tech col-span-2"
                placeholder="Company name"
                value={client.company}
                onChange={(e) => setClient({ ...client, company: e.target.value })}
              />
              <input
                className="input-tech"
                placeholder="Contact"
                value={client.name}
                onChange={(e) => setClient({ ...client, name: e.target.value })}
              />
              <input
                className="input-tech"
                placeholder="Phone / email"
                value={client.contact}
                onChange={(e) => setClient({ ...client, contact: e.target.value })}
              />
              <input
                className="input-tech col-span-2"
                placeholder="Address"
                value={client.address}
                onChange={(e) => setClient({ ...client, address: e.target.value })}
              />
              <input
                className="input-tech col-span-2"
                placeholder="GSTIN"
                value={client.gstin}
                onChange={(e) => setClient({ ...client, gstin: e.target.value })}
              />
              <textarea
                className="input-tech col-span-2 h-20 py-2"
                placeholder="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* RESULT PANEL */}
        <section className="xl:col-span-3 bg-white p-6" data-testid="mz-result">
          <div className="flex items-center justify-between mb-5">
            <div className="label-tech">2 · Layout & 4 · Quote {computing && <span className="ml-2 text-brand">computing…</span>}</div>
            {result && (
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="mz-save-btn">
                <Save size={16} /> {saving ? "Saving…" : "Save quotation"}
              </button>
            )}
          </div>

          {!result ? (
            <div className="panel-heavy p-10 min-h-[400px] flex items-center justify-center bp-grid">
              <div className="text-center">
                <div className="mono text-6xl font-bold text-ink-faint mb-4">—</div>
                <div className="label-tech">Enter dimensions to begin</div>
              </div>
            </div>
          ) : (
            <div className="panel-heavy" data-testid="mz-spec">
              {/* Summary strip */}
              <LayoutSummary result={result} />

              {/* Drawing */}
              <div className="px-6 py-5 border-t-2 border-ink bg-white" data-testid="mz-diagram">
                <div className="flex items-center justify-between mb-3">
                  <div className="label-tech">Top-view layout (mm)</div>
                  <div className="mono text-[0.7rem] text-ink-muted">
                    {result.bays_L}×{result.bays_W} bays · main beams along {result.main_beam_axis}
                  </div>
                </div>
                <MezzanineTopView result={result} />
              </div>

              {result.warnings?.length > 0 && (
                <div className="px-6 py-3 border-t border-line bg-amber-50 flex gap-2 items-start">
                  <AlertTriangle size={14} className="text-amber-600 mt-0.5" />
                  <div className="text-xs text-amber-900">{result.warnings.join(" · ")}</div>
                </div>
              )}

              {/* BOM */}
              <table className="w-full grid-lines border-t-2 border-ink">
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Section</th>
                    <th className="num">Approx unit wt</th>
                    <th className="num">Qty</th>
                    <th className="num">Total wt</th>
                  </tr>
                </thead>
                <tbody>
                  {result.components.map((c) => (
                    <tr key={c.component}>
                      <td className="font-bold uppercase tracking-wider text-xs">{c.component}</td>
                      <td className="mono text-xs">{c.section}</td>
                      <td className="num">{fmtNum(c.unit_weight_kg, 2)} kg</td>
                      <td className="num">{c.quantity}</td>
                      <td className="num font-bold">{fmtNum(c.total_weight_kg, 2)} kg</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="px-4 sm:px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-3 border-t-2 border-ink">
                <div>
                  <div className="label-tech">Total weight</div>
                  <div className="mono text-2xl font-bold mt-1">{fmtNum(result.total_weight_kg, 2)} kg</div>
                </div>
                <div>
                  <div className="label-tech">Rate</div>
                  <div className="mono text-2xl font-bold mt-1">
                    ₹{parseFloat(Number(result.price_per_kg).toFixed(2))} / kg
                  </div>
                </div>
                <div>
                  <div className="label-tech">Total price</div>
                  <div className="display font-black text-3xl text-brand mt-1">{fmtINR(result.total_price)}</div>
                </div>
              </div>

              {!isDealer && (
                <div className="px-6 py-4 border-t border-line bg-surfaceAlt">
                  <div className="label-tech mb-2">Formulas applied</div>
                  <ul className="text-xs text-ink-muted space-y-1 mono">
                    {result.components
                      .filter((c) => c.note)
                      .map((c, i) => (
                        <li key={i}>
                          <span className="font-bold text-ink">{c.component}:</span> {c.note}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

// --------------------------- Layout Summary ---------------------------
function LayoutSummary({ result }) {
  const stat = (label, val, tid) => (
    <div className="px-4 py-3 border-r border-line last:border-r-0" data-testid={tid}>
      <div className="label-tech text-[0.6rem]">{label}</div>
      <div className="mono text-base font-bold text-ink">{val}</div>
    </div>
  );
  const cols = (result.bays_L + 1) * (result.bays_W + 1);
  const bw_L = result.geometry?.bay_widths_L_mm || [];
  const bw_W = result.geometry?.bay_widths_W_mm || [];
  const fmtBays = (arr) =>
    arr.length === 0
      ? "—"
      : arr.every((v) => Math.abs(v - arr[0]) < 1)
      ? `${arr.length} × ${Math.round(arr[0])}`
      : arr.map((v) => Math.round(v)).join(" + ");
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 border-b-2 border-ink bg-surfaceAlt" data-testid="mz-summary">
      {stat("Bays", `${result.bays_L} × ${result.bays_W}`, "mz-stat-bays")}
      {stat("Pillars", cols, "mz-stat-pillars")}
      {stat("Bay (L)", fmtBays(bw_L), "mz-stat-bayL")}
      {stat("Bay (W)", fmtBays(bw_W), "mz-stat-bayW")}
      {stat(
        "Flooring",
        `${result.components.find((c) => c.component === "Flooring")?.quantity || 0} panels`,
        "mz-stat-floor"
      )}
      {stat("Railing", `${fmtNum(result.railing_total_m, 2)} m`, "mz-stat-rail")}
    </div>
  );
}



function SecondaryBeamPicker({ cfg, setCfg, update }) {
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);

  // Use the main-axis target bay as the reference for divisibility.
  const refBay = cfg.main_beam_axis === "W" ? cfg.vv_gap_W_mm : cfg.vv_gap_L_mm;

  const askSuggest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mezzanine/suggest_sec", {
        bay_mm: Number(refBay) || 3000,
        current_target_mm: Number(cfg.sec_target_mm) || 600,
        min_gap: 400,
        max_gap: 750,
      });
      setSuggestions(data?.perfect || []);
      if (!data?.perfect?.length) {
        toast.info("No perfect divisor inside 400–750 mm — last bay will keep the remainder.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const apply = (s) => {
    setCfg((p) => ({ ...p, sec_target_mm: s.spacing_mm }));
    setSuggestions([]);
    toast.success(`Secondary spacing set to ${s.spacing_mm} mm (${s.beams_per_bay} beams per ${refBay} mm bay)`);
  };

  const remainder = Number(refBay) % Number(cfg.sec_target_mm || 600);
  const showHint = remainder > 0 && remainder <= 750;
  const showWarn = remainder > 750;

  return (
    <div data-testid="mz-sec-picker">
      <div className="label-tech text-[0.65rem] mb-2">Secondary beam-to-beam distance (mm)</div>
      <div className="flex gap-2">
        <input
          type="number" min={300} max={1200} step={25}
          value={cfg.sec_target_mm}
          onChange={(e) => update("sec_target_mm")(Number(e.target.value))}
          className="input-tech mono flex-1"
          data-testid="mz-sec-target"
        />
        <button onClick={askSuggest} disabled={busy} className="btn-secondary" data-testid="mz-sec-suggest">
          <Wand2 size={12} /> {busy ? "…" : "Suggest"}
        </button>
      </div>
      {showHint && (
        <div className="text-[0.7rem] text-ink-muted mt-1.5">
          Last gap will be <b className="mono">{remainder} mm</b> in a {refBay} mm bay (within 750 mm cap).
        </div>
      )}
      {showWarn && (
        <div className="text-[0.7rem] text-red-600 mt-1.5 flex items-center gap-1">
          <AlertTriangle size={11} /> Remainder {remainder} mm exceeds 750 mm — engine will add an extra beam.
        </div>
      )}
      {suggestions.length > 0 && (
        <div className="mt-2 panel-heavy">
          <div className="px-3 py-1.5 bg-surfaceAlt border-b border-line label-tech text-[0.6rem]">
            Suggestions for {refBay} mm bay
          </div>
          <div className="p-2 grid grid-cols-2 gap-1.5">
            {suggestions.slice(0, 6).map((s, i) => (
              <button key={i} onClick={() => apply(s)} className="btn-ghost text-xs justify-start py-1.5"
                      data-testid={`mz-sec-sug-${i}`}>
                <span className="mono font-bold">{s.spacing_mm} mm</span>
                <span className="text-ink-muted ml-1">· {s.beams_per_bay} beams</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



/** Sub-chain showing each secondary-beam gap inside ONE main-axis bay.
 *  The last gap is highlighted in red if it differs from `target` (i.e. it's the remainder).
 */
